(() => {
  const MOBILE_QUERY = "(max-width: 768px)";
  const FORM_PLACEHOLDER = "CUSTOM_SIZE_FORM_ID";
  const panelCache = new Map();

  const TIP_DATA = {
    bust: {
      label: "Bust:",
      description:
        "Wear the bra you will be wearing with the dress, and pull the tape across the fullest part of the bust.",
    },
    waist: {
      label: "Waist:",
      description:
        "Bend to one side to find the crease, that's your natural waist. Pull tape across the narrowest part and keep tape slightly loose to allow for breathing room.",
    },
    hips: {
      label: "Hips:",
      description:
        "Stand straight and pull tape across the widest part of the hips and rear.",
    },
    htf: {
      label: "Hollow to Floor:",
      description:
        "Stand straight with bare feet together and measure from the center of the collarbone to the floor.",
    },
    height: {
      label: "Height (Bare Foot):",
      description:
        "Stand straight with bare feet together and measure from the top of the head to the floor.",
    },
  };

  function loadConfig() {
    const node = document.getElementById("product-custom-size-config");
    if (!node) return { sections: {}, images: {} };

    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      console.error("Product custom size config parse failed:", error);
      return { sections: {}, images: {} };
    }
  }

  function isCustomSizeValue(value) {
    const normalized = String(value || "").trim();
    if (!normalized) return false;
    return (
      normalized === "Custom Size" ||
      normalized === "Custom size" ||
      normalized.indexOf("Custom Size") === 0
    );
  }

  function isMobileViewport() {
    return window.matchMedia(MOBILE_QUERY).matches;
  }

  function getMount(variant) {
    return document.querySelector(`[data-custom-size-mount="${variant}"]`);
  }

  function applyFormId(root, formId) {
    if (!root || !formId) return;

    root.querySelectorAll(`[form="${FORM_PLACEHOLDER}"]`).forEach((node) => {
      node.setAttribute("form", formId);
    });
  }

  function executeScripts(root) {
    root.querySelectorAll("script").forEach((oldScript) => {
      const script = document.createElement("script");
      [...oldScript.attributes].forEach((attr) => {
        script.setAttribute(attr.name, attr.value);
      });
      script.textContent = oldScript.textContent;
      oldScript.replaceWith(script);
    });
  }

  function renderLoadingPlaceholder(variant = "desktop") {
    const lines = Array.from(
      { length: 5 },
      () => '<span class="custom-size-lazy__skeleton-line"></span>',
    ).join("");

    const body = `
      <div class="custom-size-lazy__placeholder" aria-hidden="true">
        <span class="custom-size-lazy__skeleton-block custom-size-lazy__skeleton-block--short"></span>
        <span class="custom-size-lazy__skeleton-block custom-size-lazy__skeleton-block--tip"></span>
        <div class="custom-size-lazy__skeleton-lines">${lines}</div>
      </div>
    `;

    if (variant === "mobile") {
      return `
        <div class="custom-size-overlay"></div>
        <div id="customize-box-mobile" class="customizebox is-mobile-modal is-open custom-size-lazy__shell">
          <div class="mobile-modal-header">
            <h3>Custom Size</h3>
            <span class="mobile-modal-close" aria-label="Close">&times;</span>
          </div>
          <div class="custom-size-lazy__shell-body">${body}</div>
        </div>
      `;
    }

    return `
      <div class="customizebox custom-size-lazy__shell" style="display:block">
        ${body}
      </div>
    `;
  }

  function setMountLoading(mount, variant) {
    if (!mount) return;
    mount.hidden = false;
    mount.setAttribute("aria-busy", "true");
    mount.innerHTML = renderLoadingPlaceholder(variant);
  }

  function closeMobileLoadingShell(mount) {
    if (!mount) return;

    document.body.classList.remove("custom-size-modal-open");
    mount.dataset.customSizeCancelled = "true";
    panelCache.delete("mobile");
    mount.removeAttribute("aria-busy");
    mount.innerHTML = "";
    mount.hidden = true;
  }

  function bindMobileLoadingShell(mount) {
    mount.addEventListener("click", (event) => {
      if (!mount.querySelector(".custom-size-lazy__shell")) return;

      if (event.target.closest(".mobile-modal-close, .custom-size-overlay")) {
        closeMobileLoadingShell(mount);
      }
    });
  }

  function openMobileLoadingShell(mount) {
    setMountLoading(mount, "mobile");
    document.body.classList.add("custom-size-modal-open");
    bindMobileLoadingShell(mount);
    window.requestAnimationFrame(() => {
      mount.querySelector("#customize-box-mobile")?.classList.add("is-open");
    });
  }

  async function fetchPanel(variant) {
    if (panelCache.has(variant)) {
      return panelCache.get(variant);
    }

    const config = loadConfig();
    const sectionId = config.sections?.[variant];
    const mount = getMount(variant);
    if (!sectionId || !mount) {
      return null;
    }

    delete mount.dataset.customSizeCancelled;

    const request = fetch(`${window.location.pathname}?section_id=${encodeURIComponent(sectionId)}`)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Unable to load custom size panel (${variant}).`);
        }
        return response.text();
      })
      .then((html) => {
        if (mount.dataset.customSizeCancelled === "true") {
          delete mount.dataset.customSizeCancelled;
          return mount;
        }

        const doc = new DOMParser().parseFromString(html, "text/html");
        const sectionRoot =
          doc.querySelector(".shopify-section") || doc.body;
        const fragment = document.createDocumentFragment();

        [...sectionRoot.childNodes].forEach((node) => {
          if (node.nodeName === "SCRIPT" || node.nodeType === Node.ELEMENT_NODE) {
            fragment.appendChild(node.cloneNode(true));
          }
        });

        applyFormId(fragment, mount.dataset.formId);
        mount.replaceChildren(...fragment.childNodes);
        mount.removeAttribute("aria-busy");
        executeScripts(mount);
        initPanel(variant, mount);
        mount.dataset.customSizeLoaded = "true";
        return mount;
      })
      .catch((error) => {
        panelCache.delete(variant);
        console.error(error);
        mount.removeAttribute("aria-busy");
        mount.innerHTML = `<p class="custom-size-lazy__error">${error.message}</p>`;
        return mount;
      });

    panelCache.set(variant, request);
    return request;
  }

  function clearDesktopCustomSizeProperties(root) {
    const scope = root || document;
    scope
      .querySelectorAll(
        ".customizebox input[name^='properties['], .customizebox textarea[name^='properties['], .customizebox select[name^='properties[']",
      )
      .forEach((input) => {
        if (input.type === "checkbox" || input.type === "radio") {
          input.checked = false;
        } else {
          input.value = "";
        }
      });
  }

  function clearMobileCustomSizeState() {
    const mobileRoot = getMount("mobile");
    if (!mobileRoot) return;

    mobileRoot
      .querySelectorAll(".text-custome-size-item-required")
      .forEach((input) => {
        input.value = "";
        input.classList.remove("customsize-input-error");
      });

    mobileRoot.querySelectorAll("textarea").forEach((textarea) => {
      textarea.value = "";
    });

    mobileRoot.querySelectorAll(".custom-size-summary-mobile").forEach((summary) => {
      summary.classList.remove("is-active");
      summary.querySelectorAll(".summary-value").forEach((node) => {
        node.textContent = "";
      });
    });
  }

  function setUnit(root, unit) {
    const next = unit === "cm" ? "cm" : "in";
    root.querySelectorAll(".customsize-unit-switch").forEach((switchNode) => {
      switchNode.dataset.unit = next;
      switchNode.querySelectorAll(".customsize-unit-label").forEach((label) => {
        label.classList.toggle("is-active", label.dataset.unit === next);
      });

      const toggle = switchNode.querySelector(".customsize-unit-toggle__input");
      if (toggle) toggle.checked = next === "cm";
    });

    root
      .querySelectorAll("[id^='customsize-measurement-unit']")
      .forEach((input) => {
        input.value = next;
      });

    root.querySelectorAll(".unit-value").forEach((label) => {
      label.textContent = next;
    });
  }

  function updateTip(root, key, config) {
    const data = TIP_DATA[key];
    if (!data) return;

    root
      .querySelectorAll("[id^='custom-size-tip-content']")
      .forEach((tipContent) => {
        const labelNode = tipContent.querySelector(".tip-label");
        const descriptionNode = tipContent.querySelector(".tip-description");
        if (labelNode) labelNode.textContent = data.label;
        if (descriptionNode) descriptionNode.textContent = data.description;
      });

    const imageUrl = config?.images?.[key] || "";
    root
      .querySelectorAll("[id^='custom-size-tip-image']")
      .forEach((image) => {
        if (!imageUrl) return;
        image.src = imageUrl;
        image.alt = `${data.label} measurement guide`;
      });
  }

  function toggleCustomSizeFields() {
    const isMobile = isMobileViewport();
    document.querySelectorAll(".desktop-only [data-custom-size-mount='desktop']").forEach((mount) => {
      mount.querySelectorAll("input, textarea, select").forEach((input) => {
        input.disabled = isMobile;
      });
    });

    document.querySelectorAll(".mobile-only [data-custom-size-mount='mobile']").forEach((mount) => {
      mount.querySelectorAll("input, textarea, select").forEach((input) => {
        input.disabled = !isMobile;
      });
    });
  }

  function openMobileModal(mobileMount) {
    const box = mobileMount.querySelector("#customize-box-mobile");
    const overlay = mobileMount.querySelector(".custom-size-overlay");
    if (!box) return;

    if (overlay) {
      overlay.style.display = "";
    }

    document.body.classList.add("custom-size-modal-open");
    box.classList.add("is-mobile-modal");
    box.style.display = "block";
    window.setTimeout(() => box.classList.add("is-open"), 10);
  }

  function closeMobileModal(mobileMount) {
    const box = mobileMount?.querySelector("#customize-box-mobile");
    if (!box) return;

    document.body.classList.remove("custom-size-modal-open");
    box.classList.remove("is-open");

    window.setTimeout(() => {
      if (!box.classList.contains("is-open")) {
        box.style.display = "none";
      }
    }, 320);
  }

  function validateRequiredMobileSizes(mobileMount) {
    const requiredIds = [
      "#bust-mobile",
      "#waist-mobile",
      "#hips-mobile",
      "#htf-mobile",
      "#height-mobile",
    ];
    let firstInvalid = null;

    requiredIds.forEach((selector) => {
      const input = mobileMount.querySelector(selector);
      if (!input) return;
      const isEmpty = !input.value || !String(input.value).trim();
      input.classList.toggle("customsize-input-error", isEmpty);
      if (isEmpty && !firstInvalid) firstInvalid = input;
    });

    if (!firstInvalid) return true;

    firstInvalid.focus({ preventScroll: true });
    return false;
  }

  function updateAndShowSummary(mobileMount) {
    const summary = mobileMount.querySelector(".custom-size-summary-mobile");
    if (!summary) return;

    let hasValues = false;

    summary.querySelectorAll(".summary-value").forEach((node) => {
      const key = node.dataset.sizeFor;
      const input = mobileMount.querySelector(`#${key}-mobile`);
      const value = input?.value?.trim();

      if (value) {
        node.textContent = value;
        hasValues = true;
      } else {
        node.textContent = "—";
      }
    });

    summary.classList.toggle("is-active", hasValues);
  }

  function showDesktopPanel(mount) {
    mount.querySelectorAll(".customizebox").forEach((box) => {
      box.style.display = "block";
    });
  }

  function hideDesktopPanel(mount) {
    clearDesktopCustomSizeProperties(mount);
    mount.querySelectorAll(".customizebox").forEach((box) => {
      box.style.display = "none";
    });
  }

  function bindDesktopPanel(mount, config) {
    if (mount.dataset.customSizeBound === "true") return;

    mount.addEventListener("change", (event) => {
      const toggle = event.target.closest(".customsize-unit-toggle__input");
      if (!toggle) return;
      setUnit(mount, toggle.checked ? "cm" : "in");
    });

    mount.addEventListener("focusin", (event) => {
      const input = event.target.closest(".text-custome-size-item-required");
      if (!input) return;
      const key = (input.id || "").replace("-mobile", "");
      updateTip(mount, key, config);
    });

    mount.addEventListener("click", (event) => {
      if (event.target.closest(".customsize-zoom-trigger")) {
        event.preventDefault();
        const modal = mount.querySelector(".customsize-zoom-modal");
        if (!modal) return;
        modal.classList.add("is-open");
        modal.setAttribute("aria-hidden", "false");
        document.body.style.overflow = "hidden";
        return;
      }

      if (
        event.target.closest(
          ".customsize-zoom-modal__close, .customsize-zoom-modal__backdrop",
        )
      ) {
        event.preventDefault();
        const modal = mount.querySelector(".customsize-zoom-modal");
        if (!modal) return;
        modal.classList.remove("is-open");
        modal.setAttribute("aria-hidden", "true");
        document.body.style.overflow = "";
      }
    });

    mount.dataset.customSizeBound = "true";
  }

  function bindMobilePanel(mount, config) {
    if (mount.dataset.customSizeBound === "true") return;

    mount.addEventListener("click", (event) => {
      if (event.target.closest(".mobile-modal-close")) {
        closeMobileModal(mount);
        return;
      }

      if (event.target.closest(".custom-size-overlay")) {
        closeMobileModal(mount);
        return;
      }

      if (event.target.closest(".btn-save-apply")) {
        if (!validateRequiredMobileSizes(mount)) return;
        if (isMobileViewport()) {
          updateAndShowSummary(mount);
        }
        closeMobileModal(mount);
        return;
      }

      if (event.target.closest(".btn-edit-sizes")) {
        openMobileModal(mount);
      }
    });

    mount.addEventListener("change", (event) => {
      const toggle = event.target.closest(".customsize-unit-toggle__input");
      if (!toggle) return;
      setUnit(mount, toggle.checked ? "cm" : "in");
    });

    mount.addEventListener("focusin", (event) => {
      const input = event.target.closest(".text-custome-size-item-required");
      if (!input) return;
      const key = (input.id || "").replace("-mobile", "");
      updateTip(mount, key, config);
    });

    mount.addEventListener("input", (event) => {
      const input = event.target.closest(".text-custome-size-item-required");
      if (!input) return;
      if (input.value && String(input.value).trim()) {
        input.classList.remove("customsize-input-error");
      }
    });

    mount.dataset.customSizeBound = "true";
  }

  function initPanel(variant, mount) {
    const config = loadConfig();
    setUnit(mount, "in");
    toggleCustomSizeFields();

    if (variant === "desktop") {
      bindDesktopPanel(mount, config);
      hideDesktopPanel(mount);
      return;
    }

    bindMobilePanel(mount, config);
    closeMobileModal(mount);
  }

  async function ensurePanel(variant) {
    const mount = getMount(variant);
    if (!mount) return null;

    if (mount.dataset.customSizeLoaded === "true") {
      return mount;
    }

    if (variant === "desktop") {
      setMountLoading(mount, "desktop");
    }

    return fetchPanel(variant);
  }

  async function showCustomSizePanels() {
    if (isMobileViewport()) {
      const mobileMount = getMount("mobile");
      if (!mobileMount) return;

      if (mobileMount.dataset.customSizeLoaded !== "true") {
        openMobileLoadingShell(mobileMount);
      }

      const loadedMount = await ensurePanel("mobile");
      if (!loadedMount?.dataset.customSizeLoaded) return;

      openMobileModal(loadedMount);
      return;
    }

    const desktopMount = await ensurePanel("desktop");
    if (desktopMount) showDesktopPanel(desktopMount);
  }

  function hideCustomSizePanels() {
    const desktopMount = getMount("desktop");
    const mobileMount = getMount("mobile");

    if (desktopMount?.dataset.customSizeLoaded === "true") {
      hideDesktopPanel(desktopMount);
    }

    if (mobileMount?.dataset.customSizeLoaded === "true") {
      clearMobileCustomSizeState();
      closeMobileModal(mobileMount);
    }
  }

  function getSelectedSizeValue(input) {
    return (
      input.value ||
      input.getAttribute("value") ||
      input.nextElementSibling?.textContent?.trim() ||
      ""
    );
  }

  async function handleVariantSelection(event) {
    const input = event.target.closest(".wt-product__options input[type='radio']");
    if (!input) return;

    if (typeof window.k_stock_text === "function") {
      window.k_stock_text();
    }

    const value = getSelectedSizeValue(input);

    if (isCustomSizeValue(value)) {
      await showCustomSizePanels();
      return;
    }

    hideCustomSizePanels();
  }

  function bindGlobalEvents() {
    if (document.documentElement.dataset.customSizeBound === "true") return;

    document.addEventListener("click", handleVariantSelection);
    document.addEventListener("change", handleVariantSelection);
    window.addEventListener("resize", toggleCustomSizeFields);

    document.addEventListener("keydown", (event) => {
      if (event.key !== "Escape") return;
      const desktopMount = getMount("desktop");
      const modal = desktopMount?.querySelector(".customsize-zoom-modal.is-open");
      if (!modal) return;
      modal.classList.remove("is-open");
      modal.setAttribute("aria-hidden", "true");
      document.body.style.overflow = "";
    });

    document.documentElement.dataset.customSizeBound = "true";
  }

  function init() {
    bindGlobalEvents();
    toggleCustomSizeFields();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  document.addEventListener("shopify:section:load", init);

  window.clearMobileCustomSizeState = clearMobileCustomSizeState;
  window.clearDesktopCustomSizeProperties = clearDesktopCustomSizeProperties;
})();
